/**
 * Admin → Customers (sub-tenant management for resellers).
 *
 * Backed by /v1/admin/customers/* — only callable when the JWT claims a
 * tenant_type of `platform` or `reseller`. The middleware returns 403 to
 * everyone else, so we don't even surface the link in the sidebar for
 * non-reseller users.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';

export interface Customer {
  id: number;
  uuid: string;
  name: string;
  type: 'platform' | 'reseller' | 'customer';
  status: 'active' | 'suspended' | 'closed';
  country?: string;
  timezone?: string;
  balance: number;
  /** Post-paid allowance. The dispatcher accepts a send while
   *  (balance + credit_limit) >= cost, so >0 turns the tenant
   *  into post-paid up to that amount. Default 0 (prepaid only). */
  credit_limit: number;
  /** Drives the in-app banner + email alerts cron. Setting to 0
   *  effectively disables both. Default 10. */
  low_balance_threshold: number;
  owner_email?: string;
  created_at: string;
  /** Per-tenant gate for the Kai post-call follow-up SMS worker
   *  (migration 000079). When false the worker skips this tenant's
   *  hot/warm leads on its next scan. Default true so all
   *  voice-enrolled tenants get follow-up SMS out of the box. */
  feature_followup_sms: boolean;
  /** Per-tenant gate for the Voice module (migration 000037).
   *  AND-ed with the global VOICE_ENABLED env in the API.
   *  Default false — flipped per-tenant in admin UI. */
  feature_voice: boolean;
  /** Per-tenant gate for the SMS module (migration 000097).
   *  Default true. Flip false for voice-only customers so the
   *  portal hides SMS sidebar items. */
  feature_sms: boolean;
  /** Per-tenant CTIA auto-reply overrides for the HELP / STOP / START
   *  SMS keywords (migration 000083). Empty string / absent = "use
   *  platform default" — the dlr-receiver treats both as fall-through
   *  to its hardcoded copy. Resellers must override the HELP body to
   *  satisfy the CTIA Short Code Monitoring Handbook (program brand
   *  identification) for their white-label customers. */
  help_template?: string;
  stop_template?: string;
  start_template?: string;
  /** SpamGuard screening posture (migration 000099):
   *  'trusted' skips the screen, 'normal' is the default, 'strict'
   *  sends clean scores to review. Read-only on the customer surface;
   *  the editable control is the platform-only
   *  PATCH /admin/tenants/{id}/spamguard-level. Absent on the list
   *  payload (only the detail GET loads it). */
  spamguard_level?: SpamguardLevel;
}

export type SpamguardLevel = 'trusted' | 'normal' | 'strict';

export interface CustomersResponse {
  items: Customer[];
  total: number;
}

export interface CreateCustomerInput {
  name: string;
  /** "customer" (default) or "reseller". Reseller-typed children can
   *  themselves have customers underneath. The /promote endpoint stays
   *  available for upgrading later. */
  tenant_type?: 'customer' | 'reseller';
  owner_email: string;
  /** Optional when `send_invite` is true. When omitted, the API
   *  generates a magic-link instead of expecting a pre-shared
   *  password. */
  owner_password?: string;
  owner_full_name?: string;
  country?: string;
  timezone?: string;
  initial_credit?: number;
  /** When true, skip the password input and email the owner a
   *  magic-link to set their own. Recommended for production
   *  onboarding. */
  send_invite?: boolean;
}

const CUSTOMERS_KEY = ['admin', 'customers'] as const;
const CUSTOMER_DETAIL_KEY = (id: number) => ['admin', 'customers', id] as const;

export function useCustomers(opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: CUSTOMERS_KEY,
    queryFn: () => api.get<CustomersResponse>('/v1/admin/customers'),
    staleTime: 30_000,
    // Defaults to enabled so the existing /admin/customers page keeps
    // its eager fetch. The tenant switcher passes `enabled: open` so it
    // doesn't hit /v1/admin/customers (which 403s for customer-tenant
    // users) on every page load.
    enabled: opts.enabled ?? true,
  });
}

// ----- Customer detail (drilldown) -----------------------------------------

export interface CustomerStats30d {
  sent: number;
  delivered: number;
  failed: number;
  /** Ratio in the [0, 1] range. UI multiplies by 100 for display. */
  delivery_rate: number;
  spend: number;
}

export interface CustomerSubUser {
  id: number;
  email: string;
  full_name?: string;
  role: 'owner' | 'admin' | 'operator' | 'viewer' | 'api';
  is_active: boolean;
  last_login_at?: string;
  created_at: string;
}

export interface CustomerCampaignSummary {
  id: number;
  uuid: string;
  name: string;
  status:
    | 'draft'
    | 'scheduled'
    | 'queued'
    | 'running'
    | 'paused'
    | 'completed'
    | 'failed'
    | 'cancelled';
  total_recipients: number;
  delivered_count: number;
  failed_count: number;
  total_cost: number;
  created_at: string;
  scheduled_at?: string;
  completed_at?: string;
}

export interface CustomerPlanRef {
  id: number;
  name: string;
  color: 'neutral' | 'volt' | 'ink';
}

/** Per-tenant TTS character budget (migration 000058). The cap is the
 *  hard monthly ceiling; the renderer fails sends with 402 once
 *  `chars_used >= cap`. `chars_used` is incremented only on successful
 *  OpenAI calls (failed renders don't burn the budget). `reset_at` is
 *  the start of the next calendar month UTC; the renderer lazy-resets
 *  on its next read after `NOW() >= reset_at`. */
export interface CustomerTTSBudget {
  cap: number;
  chars_used: number;
  reset_at: string;
}

export interface CustomerDetail {
  customer: Customer;
  stats_30d: CustomerStats30d;
  users: CustomerSubUser[];
  recent_campaigns: CustomerCampaignSummary[];
  /** Current subscription plan; absent when the customer is on
   *  pure pay-as-you-go (no plan_id assigned). */
  plan?: CustomerPlanRef;
  /** TTS character budget — drives the "TTS budget" card on the
   *  detail page. Always present (defaults from migration 000058). */
  tts_budget: CustomerTTSBudget;
}

/**
 * One-shot fetch backing the /admin/customers/:id drilldown page. The API
 * bundles header info, 30-day KPI stats, sub-users, and recent campaigns
 * so the page paints in a single round-trip instead of waterfall.
 */
export function useCustomerDetail(id: number) {
  return useQuery({
    queryKey: CUSTOMER_DETAIL_KEY(id),
    queryFn: () => api.get<CustomerDetail>(`/v1/admin/customers/${id}`),
    staleTime: 30_000,
  });
}

export interface AssignPlanResponse {
  plan?: CustomerPlanRef;
}

/**
 * Assign (planId = number) or unassign (planId = null) a plan to a
 * customer. Invalidates the customer-detail key so the page picks up
 * the new badge without a manual refetch, and the admin-plans
 * subscribers query so the MRR table re-counts.
 */
export function useAssignCustomerPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, planId }: { customerId: number; planId: number | null }) =>
      api.patch<AssignPlanResponse>(`/v1/admin/customers/${customerId}/plan`, {
        plan_id: planId,
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: CUSTOMER_DETAIL_KEY(vars.customerId) });
      void qc.invalidateQueries({ queryKey: ['admin', 'plans', 'subscribers'] });
    },
  });
}

export function useCreateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCustomerInput) =>
      api.post<Customer>('/v1/admin/customers', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CUSTOMERS_KEY });
    },
  });
}

export function useSuspendCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<Customer>(`/v1/admin/customers/${id}/suspend`),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: CUSTOMERS_KEY });
      void qc.invalidateQueries({ queryKey: CUSTOMER_DETAIL_KEY(id) });
    },
  });
}

export function useActivateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<Customer>(`/v1/admin/customers/${id}/activate`),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: CUSTOMERS_KEY });
      void qc.invalidateQueries({ queryKey: CUSTOMER_DETAIL_KEY(id) });
    },
  });
}

// ----- Manual top-up --------------------------------------------------------

export interface TopUpInput {
  customerId: number;
  amount: number;
  description?: string;
}

export interface TopUpResponse {
  new_balance: number;
}

/**
 * Manual deposit flow — used while Stripe is offline. Operator records
 * an off-platform payment (bank transfer, Zelle, cash, check) by
 * crediting the customer's balance + writing a 'deposit' transaction
 * row. Audit-logged automatically by the backend.
 */
export function useTopUpCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, amount, description }: TopUpInput) =>
      api.post<TopUpResponse>(`/v1/admin/customers/${customerId}/top-up`, {
        amount,
        description,
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: CUSTOMERS_KEY });
      void qc.invalidateQueries({ queryKey: CUSTOMER_DETAIL_KEY(vars.customerId) });
    },
  });
}

// ----- Credit limit (post-paid toggle) --------------------------------------

/**
 * Set the customer's credit_limit. The backend's parent guard means
 * a reseller can only mutate their own children; the platform admin
 * can mutate any reseller (since reseller.parent_id = 1). 0 = strict
 * prepaid; >0 = post-paid allowance.
 *
 * Returns the full Customer so the cache picks up the new value
 * without a re-fetch.
 */
export function useSetCreditLimit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, creditLimit }: { customerId: number; creditLimit: number }) =>
      api.patch<Customer>(`/v1/admin/customers/${customerId}/credit-limit`, {
        credit_limit: creditLimit,
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: CUSTOMERS_KEY });
      void qc.invalidateQueries({ queryKey: CUSTOMER_DETAIL_KEY(vars.customerId) });
    },
  });
}

// ----- Low-balance threshold ------------------------------------------------

/**
 * Set the customer's low_balance_threshold. The threshold drives the
 * in-app low-balance banner (read from /v1/billing/overview) and the
 * email-alerts cron (PR #122). Set to 0 to disable both.
 */
export function useSetLowBalanceThreshold() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, threshold }: { customerId: number; threshold: number }) =>
      api.patch<Customer>(`/v1/admin/customers/${customerId}/low-balance-threshold`, {
        low_balance_threshold: threshold,
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: CUSTOMERS_KEY });
      void qc.invalidateQueries({ queryKey: CUSTOMER_DETAIL_KEY(vars.customerId) });
    },
  });
}

// ----- Tenant type promote/demote (platform-only) ---------------------------

export type TenantTypeChangeable = 'customer' | 'reseller';

export interface ChangeTenantTypeResponse {
  id: number;
  type: TenantTypeChangeable;
  /** Server-side note: affected users must re-login for the new
   *  tenant_type to take effect in their JWT-bound session. */
  note: string;
}

/**
 * Promote a customer to reseller or demote a reseller to customer.
 * Restricted to platform admins by the backend (RequirePlatform);
 * the UI hides the control for non-platform users so this is just a
 * defense-in-depth caller — the backend rejects with 403 otherwise.
 *
 * The backend refuses to demote a reseller that still has sub-tenants
 * (would orphan them) — surfaces as 422 validation_error.
 */
export function useChangeTenantType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, newType }: { tenantId: number; newType: TenantTypeChangeable }) =>
      api.patch<ChangeTenantTypeResponse>(`/v1/admin/tenants/${tenantId}/type`, {
        type: newType,
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: CUSTOMERS_KEY });
      void qc.invalidateQueries({ queryKey: CUSTOMER_DETAIL_KEY(vars.tenantId) });
    },
  });
}

// ----- SpamGuard level (platform-only) --------------------------------------

export interface SetSpamguardLevelResponse {
  id: number;
  spamguard_level: SpamguardLevel;
}

/**
 * Set a tenant's SpamGuard screening posture. Restricted to platform
 * admins by the backend (RequirePlatform) — a reseller marking its own
 * tenant 'trusted' would let it bypass spam screening at campaign launch,
 * so the editable control is platform-only. The UI hides the card for
 * non-platform users; this is the defense-in-depth caller (backend 403s
 * otherwise). Invalidates the detail key so the dropdown reflects the
 * saved value without a manual refetch.
 */
export function useSetSpamguardLevel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, level }: { tenantId: number; level: SpamguardLevel }) =>
      api.patch<SetSpamguardLevelResponse>(`/v1/admin/tenants/${tenantId}/spamguard-level`, {
        level,
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: CUSTOMER_DETAIL_KEY(vars.tenantId) });
    },
  });
}

// ----- Impersonation --------------------------------------------------------

export interface ImpersonateResponse {
  access_token: string;
  expires_in: number;
  token_type: string;
  customer_id: number;
  customer_name: string;
}

/**
 * Mints a short-lived (15-min) access token that authenticates as the
 * customer's owner user. The reseller admin who initiated the call is
 * recorded in the JWT claims (imp + act_uid + act_email) for audit.
 *
 * The mutation does NOT touch the reseller's own tokens. Callers are
 * expected to open a new tab + hand off the token via the URL hash —
 * see openImpersonatedTab for the recipe.
 */
export function useImpersonate() {
  return useMutation({
    mutationFn: (customerID: number) =>
      api.post<ImpersonateResponse>(`/v1/admin/customers/${customerID}/impersonate`),
  });
}

// ----- TTS budget -----------------------------------------------------------

/**
 * Update the per-tenant TTS monthly character cap. Backend validates
 * the bound (>= 0, <= 10_000_000); 0 effectively disables TTS for the
 * tenant (the renderer treats `chars_used >= cap` as over-budget).
 *
 * Returns the fresh budget block so the cache picks up the new cap
 * without a re-fetch — the customer detail query is invalidated too
 * so the surrounding page sees the change in a single round.
 */
export function useSetTTSCap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, cap }: { customerId: number; cap: number }) =>
      api.patch<CustomerTTSBudget>(`/v1/admin/customers/${customerId}/tts-budget`, {
        cap,
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: CUSTOMER_DETAIL_KEY(vars.customerId) });
    },
  });
}

// ----- Kai follow-up SMS toggle --------------------------------------------

/**
 * Enable or disable the Kai post-call follow-up SMS for a customer.
 * When disabled, the ai_followup_sms worker skips the tenant's hot/warm
 * leads on its next scan. Invalidates both the list and detail keys so
 * every view that renders the toggle picks up the new value.
 */
export function useSetFeatureFollowupSMS() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, enabled }: { customerId: number; enabled: boolean }) =>
      api.patch<Customer>(`/v1/admin/customers/${customerId}/feature-followup-sms`, {
        enabled,
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: CUSTOMERS_KEY });
      void qc.invalidateQueries({ queryKey: CUSTOMER_DETAIL_KEY(vars.customerId) });
    },
  });
}

/**
 * Toggle the per-tenant Voice module flag. AND-ed server-side with
 * the global VOICE_ENABLED env; flipping it on for a tenant whose
 * binary boots with VOICE_ENABLED=false is a no-op at the /v1/features
 * resolution. Replaces the manual SQL UPDATE we ran per-customer.
 */
export function useSetFeatureVoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, enabled }: { customerId: number; enabled: boolean }) =>
      api.patch<Customer>(`/v1/admin/customers/${customerId}/feature-voice`, {
        enabled,
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: CUSTOMERS_KEY });
      void qc.invalidateQueries({ queryKey: CUSTOMER_DETAIL_KEY(vars.customerId) });
    },
  });
}

/**
 * Toggle the per-tenant SMS module flag. False hides SMS sidebar
 * items + routes via /v1/features. Does NOT today block direct API
 * sends — dispatcher-level enforcement is a follow-up.
 */
export function useSetFeatureSMS() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, enabled }: { customerId: number; enabled: boolean }) =>
      api.patch<Customer>(`/v1/admin/customers/${customerId}/feature-sms`, {
        enabled,
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: CUSTOMERS_KEY });
      void qc.invalidateQueries({ queryKey: CUSTOMER_DETAIL_KEY(vars.customerId) });
    },
  });
}

// ----- CTIA compliance templates (HELP/STOP/START per-tenant overrides) ----

/** Platform-default HELP/STOP/START auto-reply bodies. Returned alongside
 *  the customer on every PATCH response so the UI can render the textarea
 *  placeholders without a separate /defaults round-trip. Kept verbatim
 *  in sync with the dlr-receiver constants (workers/cmd/dlr-receiver/
 *  main.go::stopReply, startReply, helpReply). */
export interface ComplianceDefaults {
  help: string;
  stop: string;
  start: string;
}

export interface SetComplianceTemplatesInput {
  customerId: number;
  /** Pass `null` to leave the field unchanged (idempotent partial patch).
   *  Pass `""` to clear the override back to the platform default.
   *  Pass any other string to save it as the per-tenant override. */
  helpTemplate?: string | null;
  stopTemplate?: string | null;
  startTemplate?: string | null;
}

export interface SetComplianceTemplatesResponse {
  customer: Customer;
  defaults: ComplianceDefaults;
}

/**
 * Update one or more CTIA compliance template overrides for a customer.
 * Pointer-style payload (omit a field to leave it untouched, send empty
 * string to clear it). The backend's parent guard keeps a reseller
 * scoped to their own children. Invalidates both list + detail keys
 * so every consumer picks up the change.
 */
export function useSetComplianceTemplates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      customerId,
      helpTemplate,
      stopTemplate,
      startTemplate,
    }: SetComplianceTemplatesInput) => {
      // Only include keys the caller explicitly set so the JSON body
      // matches the pointer semantics on the backend (absent key =
      // leave column alone; null is also tolerated by the decoder but
      // omitting is cleaner over the wire).
      const body: Record<string, string> = {};
      if (helpTemplate !== undefined && helpTemplate !== null) {
        body.help_template = helpTemplate;
      }
      if (stopTemplate !== undefined && stopTemplate !== null) {
        body.stop_template = stopTemplate;
      }
      if (startTemplate !== undefined && startTemplate !== null) {
        body.start_template = startTemplate;
      }
      return api.patch<SetComplianceTemplatesResponse>(
        `/v1/admin/customers/${customerId}/compliance-templates`,
        body,
      );
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: CUSTOMERS_KEY });
      void qc.invalidateQueries({ queryKey: CUSTOMER_DETAIL_KEY(vars.customerId) });
    },
  });
}

/**
 * Opens a new tab pointed at the impersonation handoff page with the
 * minted access token in the URL fragment (`#token=...`). The fragment
 * never reaches the server — only the new tab's JS reads it. The handoff
 * page stores the token in sessionStorage (per-tab) and navigates to
 * the dashboard, so the original tab keeps the reseller session intact.
 */
export function openImpersonatedTab(accessToken: string, customerID: number, customerName: string): void {
  const params = new URLSearchParams({
    token: accessToken,
    cid: String(customerID),
    cname: customerName,
  });
  // Use the hash so the token doesn't show up in any access log along
  // the way; the SPA reads it client-side.
  const url = `${window.location.origin}/impersonate#${params.toString()}`;
  window.open(url, '_blank', 'noopener');
}
